/**
 * TOSS Web Application - Client portal sidebar
 *
 * Desktop and tablet navigation of the customer portal — the portal's half of the shared rail
 * (`components/sidebar/**`), which the internal application fills with its own menu. Since
 * 2026-08-26 the frame, the row, the flyout and the account block are that layer's; what is here
 * is what only a customer has.
 *
 * Its own, and deliberately so:
 *
 *   - **«Новая заявка»** above the menu, and this is the only place it exists on a computer
 *     (owner decision, 25.08.2026). It used to be a button in the FLOW of the dashboard — one
 *     screen out of nine — which is a strange place for the portal's primary action. Here it is
 *     where a mail client puts «Написать»: one fixed spot, reachable from every screen, above the
 *     menu rather than inside it, because it is an action and not a destination. On the phone none
 *     of this renders: there it is `ClientCreateFab`, under the thumb.
 *   - **The menu as a tree** (`clientSidebarTree`): «Справочники» and «Компания» are rows that
 *     expand, the model the owner chose for both surfaces.
 *   - **Unread badges** from `useClientNavBadges`, and the capability filter — a client session
 *     holds no INTERNAL permission keys by design (decision Р31), so it has its own.
 *   - **No ⌘K search**: `CommandPalette` is internal tooling and `ClientLayout` never mounts it.
 *
 * No company block here since 2026-08-24: `ClientHeader` names the legal entity and switches it,
 * and one name printed twice on one screen is the defect Р33 was written against.
 */

import { forwardRef, useMemo, type HTMLAttributes } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { ROUTES } from '@/constants';
import { useClientNavBadges } from '@/hooks/api/clientPortal/useClientBadges';
import { useClientCapabilities } from '@/hooks/api/clientPortal/useClientCapabilities';
import { BrandWordmark, BRAND_LABELS, BRAND_SUBTITLES } from '@/components/auth';
import {
  SidebarBrand,
  SidebarNav,
  SidebarShell,
  Tooltip,
  type SidebarNavNode,
} from '@/components/sidebar';
import {
  clientNavigationItems,
  clientSidebarTree,
  isClientNavItemVisible,
  isClientNavItemActive,
  type ClientNavItem,
} from './client-nav/clientNavigationConfig';
import { ClientAccountBlock } from './client-nav/ClientAccountBlock';

// ============================================================================
// Types
// ============================================================================

export interface ClientSidebarProps extends HTMLAttributes<HTMLElement> {
  /** Additional class names */
  className?: string;
  /** Icon-only mode (always on tablet, opt-in on desktop) */
  collapsed?: boolean;
  /** Collapse toggle handler. Omitted on tablet, where the collapse is forced. */
  onToggleCollapsed?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export const ClientSidebar = forwardRef<HTMLElement, ClientSidebarProps>(
  ({ className = '', collapsed = false, onToggleCollapsed, ...props }, ref) => {
    const location = useLocation();
    const { notificationsCount, chatCount } = useClientNavBadges();
    const caps = useClientCapabilities();

    // The tree hands the shared layer plain nodes, and «is this the current screen?» needs the
    // item's own `exact` flag — «Главная» is `/client`, a prefix of every other portal route. The
    // lookup gets it back by id; every leaf and child of the tree comes from this array.
    const byId = useMemo(
      () => new Map(clientNavigationItems.map((item) => [item.id, item])),
      []
    );

    const badgeFor = (itemId: string): number | undefined => {
      if (itemId === 'client-messenger') return chatCount || undefined;
      if (itemId === 'client-notifications') return notificationsCount || undefined;
      return undefined;
    };

    // Filtered here rather than in the config, which is a module-level constant and has no session
    // to ask. A parent left with no visible children is dropped whole — otherwise «Компания» would
    // be a row that opens onto nothing for every ordinary employee.
    const toNode = (item: ClientNavItem): SidebarNavNode | null => {
      const children = (item.children as ClientNavItem[] | undefined)
        ?.filter((child) => isClientNavItemVisible(child, caps.can))
        .map((child) => ({
          id: child.id,
          label: child.label,
          path: child.path,
          icon: child.icon,
          badgeCount: badgeFor(child.id),
        }));

      if (item.children && (!children || children.length === 0)) return null;
      if (!item.children && !isClientNavItemVisible(item, caps.can)) return null;

      return {
        id: item.id,
        label: item.label,
        path: item.path,
        icon: item.icon,
        badgeCount: badgeFor(item.id),
        children,
      };
    };

    const items = clientSidebarTree
      .map(toNode)
      .filter((node): node is SidebarNavNode => node !== null);

    return (
      <SidebarShell
        ref={ref}
        className={className}
        collapsed={collapsed}
        navLabel="Основное меню"
        brand={
          <SidebarBrand
            to={ROUTES.client.dashboard}
            /* Тот же лок-ап, что печатает дверь кабинета: имя набирается в одном месте. */
            title={<BrandWordmark surface="client" />}
            subtitle={BRAND_SUBTITLES.client}
            label={BRAND_LABELS.client}
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
          />
        }
        top={
          /* Кнопка белая, а не брендовая синяя: с 27.08.2026 рейл сам брендовый тёмный, и
             `primary-500` на нём пропадает. Инверсия — та же, что у главной кнопки в шапке
             тёмных панелей: белое пятно на тёмном читается как главное действие. */
          <div className={`flex-shrink-0 pb-3 ${collapsed ? 'px-2' : 'px-3'}`}>
            {collapsed ? (
              <Tooltip content="Новая заявка" side="right">
                <Link
                  to={ROUTES.client.applications.create}
                  aria-label="Новая заявка"
                  className="mx-auto flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--sidebar-cta-bg)] text-[var(--sidebar-cta-text)] hover:bg-[var(--sidebar-cta-hover-bg)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sidebar-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--sidebar-bg)]"
                >
                  <Plus className="w-5 h-5" aria-hidden="true" />
                </Link>
              </Tooltip>
            ) : (
              <Link
                to={ROUTES.client.applications.create}
                className="flex items-center justify-center gap-2 w-full h-10 rounded-lg bg-[var(--sidebar-cta-bg)] text-[var(--sidebar-cta-text)] text-sm font-medium hover:bg-[var(--sidebar-cta-hover-bg)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sidebar-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--sidebar-bg)]"
              >
                <Plus className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                Новая заявка
              </Link>
            )}
          </div>
        }
        footer={<ClientAccountBlock collapsed={collapsed} />}
        {...props}
      >
        <SidebarNav
          items={items}
          currentPath={location.pathname}
          // The portal's own rule: «Главная» matches `/client` exactly, since its path is a prefix
          // of every other route in the portal.
          isActive={(node, pathname) => {
            const item = byId.get(node.id);
            if (item) return isClientNavItemActive(item, pathname);
            return pathname === node.path || pathname.startsWith(`${node.path}/`);
          }}
          collapsed={collapsed}
        />
      </SidebarShell>
    );
  }
);

ClientSidebar.displayName = 'ClientSidebar';

export default ClientSidebar;
